import { Wrapper } from './styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import { IconSize, IconVariant } from 'components/shared/Icon/types'
import { Color, LinkVariant, TextVariant } from 'styles/types'
import { useNavigate } from 'react-router-dom'
import React, {
    HTMLAttributeAnchorTarget,
    MouseEvent,
    KeyboardEvent,
} from 'react'
import { DefaultTheme } from 'styled-components'

interface LinkProps {
    children: React.ReactNode
    path?: string
    to?: string
    onClick?: () => void
    icon?: null | IconVariant
    iconPosition?: 'left' | 'right'
    iconRotation?: number
    iconColor?: Color
    iconSize?: IconSize | [IconSize, IconSize]
    variant: LinkVariant
    textVariant?: TextVariant
    isDisabled?: boolean
    className?: string
    id?: string
    target?: HTMLAttributeAnchorTarget
    theme?: DefaultTheme
    isUnderlined?: boolean
    shouldStopPropagation?: boolean
    focusable?: boolean
    'data-testid'?: string
}

const Link = ({
    children,
    path,
    to,
    onClick,
    icon = 'arrow',
    iconPosition = 'right',
    iconRotation,
    variant = 'primary',
    textVariant = 'bodyStrong',
    isDisabled = false,
    className,
    id,
    target,
    iconSize = [20, 24],
    iconColor,
    theme,
    isUnderlined = false,
    shouldStopPropagation,
    focusable = true,
    'data-testid': testId,
}: LinkProps) => {
    const navigate = useNavigate()

    const doLinkActions = () => {
        to && navigate(to)
        onClick && onClick()
    }

    const handleOnClick = (event: MouseEvent<HTMLElement>) => {
        shouldStopPropagation && event.stopPropagation()
        doLinkActions()
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLAnchorElement>) =>
        event.key === 'Enter' && doLinkActions()

    return (
        <Wrapper
            href={path}
            onClick={handleOnClick}
            onKeyDown={handleKeyDown}
            variant={variant}
            isDisabled={isDisabled}
            className={className}
            id={id}
            target={target}
            theme={theme}
            isUnderlined={isUnderlined}
            data-testid={testId}
            tabIndex={focusable ? 0 : -1}
        >
            <Box
                direction={iconPosition === 'left' ? 'row-reverse' : 'row'}
                align="center"
                justify="center"
                gap={[8, 12]}
                as="span"
            >
                <Text
                    variant={textVariant}
                    as="span"
                    color="grayscale_400"
                    theme={theme}
                >
                    {children}
                </Text>
                {icon && (
                    <Icon
                        variant={icon}
                        rotation={iconRotation}
                        size={iconSize}
                        color={iconColor}
                        theme={theme}
                    />
                )}
            </Box>
        </Wrapper>
    )
}

export default Link
